import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Kicker, Tag } from "./Reveal";

const ANGEL = {
  name: "Angel",
  role: "Founder, CEO & Lead Narrative Therapist",
  about:
    "Narrative therapist and community practitioner — and a mother, kinship carer, foster carer and Aunty. Angel built Ngawilyi after more than seven years alongside children, young people and families, seeing the space between systems that an Aunty could bridge.",
  qualifications: [
    "Masters of Narrative Therapy and Community Work — University of Melbourne",
    "Diploma of Narrative Therapy and Community Work",
    "University Certificate in Trauma",
    "Certificate IV in Child, Youth and Family Intervention",
    "Certificate IV in Youth Work",
    "Certificate III in Fitness",
    "Certificate III in Government and Business",
    "Certificate in Treating Trauma Using Dialectical Behaviour Therapy (DBT)",
    "Suicide First Aid",
  ],
  interests: ["Children & young people", "Family & carer relationships", "Identity & belonging", "Culture & connection", "Mentoring & movement"],
  populations: "Children, young people, parents, kinship carers, foster carers and families",
  cultural: "Aboriginal practitioner, grounded in Aboriginal ways of knowing, being and doing",
  supports: ["Narrative Therapy", "Mentoring", "Movement-based support"],
  availability: "Availability confirmed when you request support",
  funding: "Private · NDIS · Service-funded",
  delivery: "In person · Community · Telehealth",
};

const FIELDS = [
  "Name", "Role", "About Me", "Qualifications", "Practice interests",
  "Populations worked with", "Cultural / community experience", "Support types",
  "Session availability", "Private / NDIS / service-funded availability",
  "In-person / community / telehealth availability",
];

const ProfileCard = ({ p, requestLink, placeholder }) => (
  <div className={`rounded-[36px] p-8 h-full flex flex-col ${placeholder ? "bg-cream border border-ochre/30" : "bg-creamdark border border-ochre/30 shadow-[0_20px_40px_rgba(74,55,40,0.07)]"}`}>
    <div className={`organic-blob aspect-[5/4] grain relative flex items-center justify-center ${placeholder ? "bg-gradient-to-br from-blush via-ochre/50 to-sunset/60" : "bg-gradient-to-br from-terracotta via-sunset to-ochre"}`}>
      <p className={`font-display italic text-lg ${placeholder ? "text-earth/70" : "text-cream/90"}`}>Photo coming soon</p>
    </div>
    {placeholder ? (
      <>
        <h3 className="mt-6 font-display text-2xl text-charcoal">More Practitioners Coming</h3>
        <div className="mt-5 space-y-2.5 flex-1">
          {FIELDS.map((f) => (
            <div key={f} className="border-b border-dashed border-ochre/40 pb-2">
              <p className="text-xs text-earth/55">{f}</p>
            </div>
          ))}
        </div>
        <div className="mt-6"><Tag tone="ochre">Placeholder Profile</Tag></div>
      </>
    ) : (
      <>
        <h3 className="mt-6 font-display text-3xl text-charcoal">{p.name}</h3>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mt-1">{p.role}</p>
        <p className="mt-4 text-sm leading-relaxed text-earth/85">{p.about}</p>
        <p className="mt-5 text-[11px] uppercase tracking-[0.2em] font-bold text-earth/60">Qualifications</p>
        <ul className="mt-2 space-y-1.5">
          {p.qualifications.map((q) => (
            <li key={q} className="text-sm text-earth flex gap-2.5">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />{q}
            </li>
          ))}
        </ul>
        <p className="mt-5 text-[11px] uppercase tracking-[0.2em] font-bold text-earth/60">Practice interests</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {p.interests.map((i) => (
            <span key={i} className="rounded-full bg-cream border border-ochre/40 px-3 py-1 text-xs font-semibold text-earth">{i}</span>
          ))}
        </div>
        <div className="mt-5 space-y-2 text-sm text-earth/80 flex-1">
          <p><span className="font-bold text-charcoal">Works with:</span> {p.populations}</p>
          <p><span className="font-bold text-charcoal">Cultural experience:</span> {p.cultural}</p>
          <p><span className="font-bold text-charcoal">Support types:</span> {p.supports.join(" · ")}</p>
          <p><span className="font-bold text-charcoal">Availability:</span> {p.availability}</p>
          <p><span className="font-bold text-charcoal">Funding pathways:</span> {p.funding}</p>
          <p><span className="font-bold text-charcoal">Delivery:</span> {p.delivery}</p>
        </div>
        <a
          data-testid="request-practitioner-btn"
          href={requestLink}
          className="group mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-xs font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105"
        >
          Request This Practitioner <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </a>
      </>
    )}
  </div>
);

export const Practitioners = ({ kicker = "Meet Our Practitioners", title = "The people beside you.", requestLink = "#request-form" }) => (
  <section className="px-6 py-20 sm:py-28 bg-cream" data-testid="practitioners-section">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-12">
        <Kicker>{kicker}</Kicker>
        <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">{title}</h2>
        <p className="mt-6 text-earth/85 leading-relaxed max-w-2xl">
          Our team is growing carefully and intentionally. Practitioner preferences are subject to availability —
          and a qualification never means a support is automatically claimable; we confirm funding arrangements
          before services begin.
        </p>
      </Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
        <Reveal><ProfileCard p={ANGEL} requestLink={requestLink} /></Reveal>
        <Reveal delay={0.1}><ProfileCard placeholder /></Reveal>
        <Reveal delay={0.2}><ProfileCard placeholder /></Reveal>
      </div>
    </div>
  </section>
);
